/**
 * DriverCollectCardModal — Driver-side passenger CC capture BEFORE starting a
 * Group / City / Custom / Meter trip.
 *
 * Driver collects: First name · Last name · Phone · Square card token
 * On token: POST /api/driver/charge-passenger (immediate capture, autocomplete=true).
 * On success: returns { payment_id, last4, brand, passenger:{...} } via onComplete()
 * so the calling page can store it on the trip and unlock "Save Trip" / "Start Meter".
 *
 * Two collection modes (per user choice):
 *   • Web SDK    — Square Web Payments SDK iframe in this browser (works on driver's phone)
 *   • Terminal   — Mark "in-person" (Square Terminal API hardware integration TBD)
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SquareCardForm from "@/components/SquareCardForm";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtUsd } from "@/lib/format";
import { Loader2, CreditCard, Smartphone, CheckCircle2, ShieldCheck } from "lucide-react";

export default function DriverCollectCardModal({ amountCents, rideId = null, triggerLabel = "Collect Card · Pre-charge", onComplete, disabled = false, autoOpen = false }) {
  const [open, setOpen] = useState(autoOpen);
  const [mode, setMode] = useState("web");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { payment_id, last4, brand, passenger }

  const reset = () => { setFirst(""); setLast(""); setPhone(""); setBusy(false); setDone(null); };

  const closeAfterSuccess = () => {
    if (done) onComplete?.(done);
    setOpen(false);
    setTimeout(reset, 300);
  };

  const submitFromSquareToken = async (token, details) => {
    if (!first.trim() || !last.trim() || !phone.trim()) {
      toast.error("Please fill First, Last and Phone first");
      return;
    }
    if (!amountCents || amountCents <= 0) {
      toast.error("Calculate fare before collecting card");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/driver/charge-passenger", {
        first_name: first.trim(),
        last_name: last.trim(),
        phone: phone.trim(),
        amount_cents: amountCents,
        source_id: token,
        ride_id: rideId,
        note: `Driver-collected · ${first} ${last}`,
      });
      const last4 = details?.card?.last4 || "";
      const brand = details?.card?.brand || "CARD";
      const result = { ...data, last4, brand, passenger: data.passenger };
      setDone(result);
      toast.success("Card charged · funds captured");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Charge failed");
    } finally { setBusy(false); }
  };

  const markInPerson = async () => {
    // Square Terminal API path: send a checkout to the paired Square Terminal
    // device. The Terminal handles EMV chip / contactless tap / swipe natively.
    if (!first.trim() || !last.trim() || !phone.trim()) {
      toast.error("Please fill First, Last and Phone first");
      return;
    }
    if (!amountCents || amountCents <= 0) {
      toast.error("Calculate fare before collecting card");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/driver/terminal/charge", {
        amount_cents: amountCents,
        ride_id: rideId,
        first_name: first.trim(), last_name: last.trim(), phone: phone.trim(),
        note: `Buller Taxi · Terminal · ${first} ${last}`,
      });
      // Poll the checkout until COMPLETED or CANCELED (paired Terminal handles
      // the chip/tap/swipe; we just wait for Square to confirm).
      const checkoutId = data.checkout_id;
      let status = data.status || "PENDING";
      const startedAt = Date.now();
      while (status !== "COMPLETED" && status !== "CANCELED" && Date.now() - startedAt < 180000) {
        await new Promise(r => setTimeout(r, 4000));
        try {
          const { data: poll } = await api.get(`/driver/terminal/checkout/${checkoutId}`);
          status = poll.status || status;
        } catch { break; }
      }
      if (status !== "COMPLETED") {
        toast.error(`Terminal checkout ${status?.toLowerCase()}`);
        return;
      }
      const result = {
        payment_id: checkoutId,
        last4: "TERM",
        brand: "TERMINAL",
        passenger: { first_name: first.trim(), last_name: last.trim(), phone: phone.trim() },
        _terminal: true,
      };
      setDone(result);
      toast.success("Terminal charge complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Terminal charge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <Button
        data-testid="open-collect-card-modal"
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
      >
        <CreditCard className="h-4 w-4 mr-2"/>{triggerLabel} · {fmtUsd((amountCents||0)/100)}
      </Button>
      <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-amber-400 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5"/>Pre-charge Passenger Card
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="space-y-3 py-2" data-testid="collect-card-success">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="h-6 w-6"/>
              <span className="font-bold">Payment captured</span>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded p-3 space-y-1 text-sm">
              <Row k="Passenger" v={`${done.passenger.first_name} ${done.passenger.last_name}`}/>
              <Row k="Phone" v={done.passenger.phone}/>
              {done.last4 && <Row k="Card" v={`${done.brand?.toUpperCase()} •••• ${done.last4}`}/>}
              {done.payment_id && <Row k="Payment ID" v={done.payment_id}/>}
              <Row k="Amount" v={fmtUsd((amountCents||0)/100)}/>
            </div>
            <Button
              data-testid="collect-card-continue-btn"
              onClick={closeAfterSuccess}
              className="w-full h-11 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
            >
              Continue → Start Trip
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-zinc-400">Collect the rider's name, phone and card BEFORE starting the trip. This locks the booking and prevents disputes.</p>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400">First name</Label>
                <Input data-testid="collect-first-name" value={first} onChange={e=>setFirst(e.target.value)} placeholder="Jane" className="bg-zinc-950 border-zinc-700 h-10 mt-1"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Last name</Label>
                <Input data-testid="collect-last-name" value={last} onChange={e=>setLast(e.target.value)} placeholder="Doe" className="bg-zinc-950 border-zinc-700 h-10 mt-1"/>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Phone</Label>
              <Input data-testid="collect-phone" type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="(580) 555-0123" className="bg-zinc-950 border-zinc-700 h-10 mt-1"/>
            </div>

            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="bg-zinc-950 grid grid-cols-2 h-9">
                <TabsTrigger value="web" data-testid="collect-mode-web" className="text-xs"><Smartphone className="h-3 w-3 mr-1"/>Web SDK</TabsTrigger>
                <TabsTrigger value="terminal" data-testid="collect-mode-terminal" className="text-xs"><CreditCard className="h-3 w-3 mr-1"/>Terminal</TabsTrigger>
              </TabsList>
              <TabsContent value="web" className="mt-3">
                <p className="text-[11px] text-zinc-500 mb-2">Hand the phone to the passenger to enter their card. Charged immediately for {fmtUsd((amountCents||0)/100)}.</p>
                {busy ? (
                  <div className="flex items-center justify-center py-6 text-amber-400"><Loader2 className="h-6 w-6 animate-spin"/></div>
                ) : (
                  <SquareCardForm onToken={submitFromSquareToken} busy={busy} label={`CHARGE ${fmtUsd((amountCents||0)/100)}`}/>
                )}
              </TabsContent>
              <TabsContent value="terminal" className="mt-3">
                <p className="text-[11px] text-zinc-500 mb-2">Send the charge to your paired Square Terminal. Driver hands the rider the Terminal — EMV chip, contactless tap, and swipe all work natively. We poll Square until COMPLETED.</p>
                <Button
                  data-testid="collect-terminal-btn"
                  onClick={markInPerson}
                  disabled={busy || !first || !last || !phone}
                  className="w-full h-11 bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-amber-400/40 font-bold"
                >
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Awaiting Terminal…</> : `Send ${fmtUsd((amountCents||0)/100)} to Square Terminal`}
                </Button>
                <p className="text-[10px] text-zinc-600 mt-2 italic">Pair a Square Terminal once via your Square Dashboard → Devices and save the device ID in admin → Payments → Square. Phone-attached Bluetooth readers (S017/S025) require a future native app build.</p>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-zinc-500">{k}</span>
      <span className="text-zinc-100 font-mono text-right break-all">{v}</span>
    </div>
  );
}
